import type { KnownRoute } from "@/Routing/KnownRoute";
import { routeMatches } from "@/Routing/routeMatches";

// URLs customers guess from the feature's name. The menu says "Customs
// Brokers", so people type /customs-brokers.
const ROUTE_ALIASES: Record<string, string> = {
  "/customs-brokers": "/brokers",
};

interface ResolveRouteRedirectInput {
  pathname: string;
  search: string;
  isAuthenticated: boolean;
  signedInRoutes: Array<KnownRoute>;
  openRoutes: Array<KnownRoute>;
}

interface RouteRedirect {
  to: string;
  // Where sign-in should send the customer once they're in.
  afterSignIn?: string | undefined;
}

/** Where to send a URL no screen would render for. Every Page matches the URL
 *  on its own, so without this an unknown URL shows an empty page that looks
 *  like the app is down. Returns undefined when a screen will render. */
export function resolveRouteRedirect(
  input: ResolveRouteRedirectInput,
): RouteRedirect | undefined {
  const { pathname, search, isAuthenticated, signedInRoutes, openRoutes } =
    input;
  const signedOutHome = "/book/details";

  const alias = ROUTE_ALIASES[pathname];
  if (alias !== undefined) {
    return { to: `${alias}${search}` };
  }

  // Open routes go first: /connectivity-test is in both lists and must keep
  // working signed out.
  if (openRoutes.some((r) => routeMatches(pathname, r.path, r.exact))) {
    return undefined;
  }

  if (signedInRoutes.some((r) => routeMatches(pathname, r.path, r.exact))) {
    if (isAuthenticated) {
      return undefined;
    }
    if (pathname === "/") {
      return { to: signedOutHome };
    }
    // Usually an expired session opening a bookmark or an emailed link.
    return { to: "/sign-in", afterSignIn: `${pathname}${search}` };
  }

  return { to: isAuthenticated ? "/" : signedOutHome };
}
